/**
 * Account Review Detail Page
 * Create/edit account reviews with comments, audit trail, and reviewer workflow
 * Sections: Information, Other Items, Retailer Materials, Technology & Software,
 *           Events & Opportunities, Comment, Activity
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAccountReview,
  createAccountReview,
  updateAccountReview,
  deleteAccountReview,
  getReviewComments,
  createReviewComment,
  findReviewerForPosition,
  findVPOfSales,
  getSalesPositions,
} from "../services/salesTeam.service";
import {
  searchOrganizations,
  getOrganization,
} from "../services/organizations.service";
import { createTask } from "../services/tasks.service";
import { TaskPriority, TaskStatus } from "../types/task.types";
import { useAuth } from "../contexts/AuthContext";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Badge from "../components/ui/Badge";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  ChatBubbleLeftIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
// UUID used in service function signatures
import type {
  MaterialStatus,
  ReviewStatus,
  CreateAccountReviewInput,
} from "../types/sales.types";
import { REVIEW_STATUS_OPTIONS } from "../types/sales.types";

// Helper: Yes/No/N-A button group
const MaterialStatusGroup: React.FC<{
  value: MaterialStatus | null;
  onChange: (v: MaterialStatus) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <div className="flex gap-1">
    {(
      [
        {
          val: "yes",
          label: "Yes",
          color: "bg-green-100 text-green-800 border-green-400",
        },
        {
          val: "no",
          label: "No",
          color: "bg-red-100 text-red-800 border-red-400",
        },
        {
          val: "na",
          label: "N/A",
          color: "bg-gray-100 text-gray-600 border-gray-400",
        },
      ] as const
    ).map((opt) => (
      <button
        key={opt.val}
        type="button"
        disabled={disabled}
        onClick={() => onChange(opt.val)}
        className={`px-3 py-1 text-xs rounded border transition-colors ${
          value === opt.val
            ? opt.color
            : "bg-white border-gray-200 text-gray-500 hover:border-gray-400"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

// Helper: Yes/No toggle
const BooleanToggle: React.FC<{
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <div className="flex gap-1">
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(true)}
      className={`px-3 py-1 text-xs rounded border transition-colors ${
        value
          ? "bg-green-100 text-green-800 border-green-400"
          : "bg-white border-gray-200 text-gray-500 hover:border-gray-400"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      Yes
    </button>
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(false)}
      className={`px-3 py-1 text-xs rounded border transition-colors ${
        !value
          ? "bg-red-100 text-red-800 border-red-400"
          : "bg-white border-gray-200 text-gray-500 hover:border-gray-400"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      No
    </button>
  </div>
);

// Status icon for view mode
const StatusIcon: React.FC<{ value: boolean | MaterialStatus | null }> = ({
  value,
}) => {
  if (value === true || value === "yes") {
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
  }
  if (value === false || value === "no") {
    return <XCircleIcon className="h-5 w-5 text-red-500" />;
  }
  if (value === "na") {
    return <MinusCircleIcon className="h-5 w-5 text-gray-400" />;
  }
  return <MinusCircleIcon className="h-5 w-5 text-gray-300" />;
};

const AccountReviewDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isNew = !id || id === "create";
  const preselectedPositionId = searchParams.get("positionId") || "";
  const preselectedOrgId = searchParams.get("orgId") || "";

  const [isEditing, setIsEditing] = useState(isNew);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newComment, setNewComment] = useState("");

  // Organization search
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [orgSearchResults, setOrgSearchResults] = useState<
    Array<{
      id: string;
      name: string;
      address_city?: string;
      address_state?: string;
    }>
  >([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Form state
  const [organizationId, setOrganizationId] = useState(preselectedOrgId);
  const [reviewDate, setReviewDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [status, setStatus] = useState<ReviewStatus>("draft");

  // FreshService
  const [freshserviceTicketCreated, setFreshserviceTicketCreated] =
    useState(false);

  // Other Items
  const [evidenceOfCompetitorMaterials, setEvidenceOfCompetitorMaterials] =
    useState(false);

  // Retailer Materials
  const [counterCardsOnDisplay, setCounterCardsOnDisplay] =
    useState<MaterialStatus | null>(null);
  const [acrylicStandInGoodShape, setAcrylicStandInGoodShape] =
    useState<MaterialStatus | null>(null);
  const [posters, setPosters] = useState<MaterialStatus | null>(null);
  const [banners, setBanners] = useState<MaterialStatus | null>(null);
  const [dealershieldWindowCling, setDealershieldWindowCling] =
    useState<MaterialStatus | null>(null);

  // Technology and Software
  const [counterCardQrVerification, setCounterCardQrVerification] =
    useState(false);
  const [classroomPostersQrVerification, setClassroomPostersQrVerification] =
    useState(false);
  const [promoCodeQrVerification, setPromoCodeQrVerification] = useState(false);
  const [affiliateLinkOnWebsite, setAffiliateLinkOnWebsite] = useState(false);
  const [marketingVideosPlaying, setMarketingVideosPlaying] = useState(false);
  const [usesGearfireAxisPos, setUsesGearfireAxisPos] = useState(false);

  // Events and Opportunities
  const [attendingClasses, setAttendingClasses] = useState(false);
  const [uslsCertifiedInstructors, setUslsCertifiedInstructors] =
    useState(false);

  // Comment
  const [reviewComment, setReviewComment] = useState("");

  // Fetch review data
  const { data: review, isLoading } = useQuery({
    queryKey: ["account-review", id],
    queryFn: () => getAccountReview(id!),
    enabled: !isNew && !!id,
  });

  // Fetch comments
  const { data: comments } = useQuery({
    queryKey: ["review-comments", id],
    queryFn: () => getReviewComments(id!),
    enabled: !isNew && !!id,
  });

  // Populate form from fetched data
  useEffect(() => {
    if (review) {
      setOrganizationId(review.organization_id);
      setSelectedOrg(
        review.organization
          ? { id: review.organization.id, name: review.organization.name }
          : null,
      );
      setReviewDate(review.review_date);
      setStatus(review.status);
      setFreshserviceTicketCreated(review.freshservice_ticket_created);
      setEvidenceOfCompetitorMaterials(review.evidence_of_competitor_materials);
      setCounterCardsOnDisplay(review.counter_cards_on_display);
      setAcrylicStandInGoodShape(review.acrylic_stand_in_good_shape);
      setPosters(review.posters);
      setBanners(review.banners);
      setDealershieldWindowCling(review.dealershield_window_cling);
      setCounterCardQrVerification(review.counter_card_qr_verification);
      setClassroomPostersQrVerification(
        review.classroom_posters_qr_verification,
      );
      setPromoCodeQrVerification(review.promo_code_qr_verification);
      setAffiliateLinkOnWebsite(review.affiliate_link_on_website);
      setMarketingVideosPlaying(review.marketing_videos_playing);
      setUsesGearfireAxisPos(review.uses_gearfire_axis_pos);
      setAttendingClasses(review.attending_classes);
      setUslsCertifiedInstructors(review.usls_certified_instructors);
      setReviewComment(review.review_comment || "");
    }
  }, [review]);

  // Reset form for new
  useEffect(() => {
    if (isNew) {
      setIsEditing(true);
      setOrganizationId(preselectedOrgId);
      setReviewDate(new Date().toISOString().split("T")[0]);
      setStatus("draft");
      setFreshserviceTicketCreated(false);
      setEvidenceOfCompetitorMaterials(false);
      setCounterCardsOnDisplay(null);
      setAcrylicStandInGoodShape(null);
      setPosters(null);
      setBanners(null);
      setDealershieldWindowCling(null);
      setCounterCardQrVerification(false);
      setClassroomPostersQrVerification(false);
      setPromoCodeQrVerification(false);
      setAffiliateLinkOnWebsite(false);
      setMarketingVideosPlaying(false);
      setUsesGearfireAxisPos(false);
      setAttendingClasses(false);
      setUslsCertifiedInstructors(false);
      setReviewComment("");
      setError(null);

      // Fetch org details if preselected from org page
      if (preselectedOrgId) {
        getOrganization(preselectedOrgId)
          .then((org) => {
            setSelectedOrg({ id: org.id, name: org.name });
          })
          .catch(() => {
            setSelectedOrg(null);
          });
      } else {
        setSelectedOrg(null);
      }
    }
  }, [isNew, preselectedOrgId]);

  // Org search debounce
  useEffect(() => {
    if (!orgSearchQuery.trim() || orgSearchQuery.length < 2) {
      setOrgSearchResults([]);
      return;
    }
    setOrgSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const results = await searchOrganizations(orgSearchQuery);
        setOrgSearchResults(
          results.map((o) => ({
            id: o.id,
            name: o.name,
            address_city: o.address_city ?? undefined,
            address_state: o.address_state ?? undefined,
          })),
        );
      } catch {
        setOrgSearchResults([]);
      } finally {
        setOrgSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [orgSearchQuery]);

  // Build the input for save
  const buildInput = (): CreateAccountReviewInput => ({
    organization_id: organizationId,
    sales_position_id: preselectedPositionId || undefined,
    review_date: reviewDate,
    freshservice_ticket_created: freshserviceTicketCreated,
    evidence_of_competitor_materials: evidenceOfCompetitorMaterials,
    counter_cards_on_display: counterCardsOnDisplay ?? undefined,
    acrylic_stand_in_good_shape: acrylicStandInGoodShape ?? undefined,
    posters: posters ?? undefined,
    banners: banners ?? undefined,
    dealershield_window_cling: dealershieldWindowCling ?? undefined,
    counter_card_qr_verification: counterCardQrVerification,
    classroom_posters_qr_verification: classroomPostersQrVerification,
    promo_code_qr_verification: promoCodeQrVerification,
    affiliate_link_on_website: affiliateLinkOnWebsite,
    marketing_videos_playing: marketingVideosPlaying,
    uses_gearfire_axis_pos: usesGearfireAxisPos,
    attending_classes: attendingClasses,
    usls_certified_instructors: uslsCertifiedInstructors,
    review_comment: reviewComment || undefined,
    status,
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const input = buildInput();

      if (isNew) {
        return createAccountReview(input, user?.id);
      }
      return updateAccountReview(id!, {
        ...input,
        last_edited_by: user?.id,
        last_edited_at: new Date().toISOString(),
      } as any);
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["account-review"] });
      queryClient.invalidateQueries({ queryKey: ["account-reviews"] });

      // Only when the review MOVES into "submitted" (new, or previously another status): re-saving
      // an already-submitted review must not assign again or create a duplicate reviewer task.
      // (Before 2026-10-16 the task insert always failed, which hid this.)
      const becameSubmitted =
        status === "submitted" && (isNew || review?.status !== "submitted");
      if (becameSubmitted) {
        try {
          let reviewerId: string | null = null;

          // Step 1: Try to find the user's sales position
          let positionId = preselectedPositionId || data.sales_position_id;
          if (!positionId && user?.id) {
            const positions = await getSalesPositions({ is_active: true });
            const myPosition = positions.find(
              (p) => p.assigned_profile_id === user.id,
            );
            if (myPosition) {
              positionId = myPosition.id;
              await updateAccountReview(data.id, {
                sales_position_id: myPosition.id,
              } as any);
            }
          }

          // Step 2: If user is in hierarchy, reviewer = their direct supervisor
          if (positionId) {
            reviewerId = await findReviewerForPosition(positionId);
          }

          // Step 3: Fallback — if no reviewer found (user not in hierarchy, or
          // is at top of chain), assign to VP of Sales
          if (!reviewerId) {
            const vp = await findVPOfSales();
            if (vp?.assigned_profile_id) {
              reviewerId = vp.assigned_profile_id;
            }
          }

          if (reviewerId) {
            await updateAccountReview(data.id, {
              reviewer_id: reviewerId,
            } as any);

            await createTask({
              title: `Review account review: ${selectedOrg?.name || "Organization"}`,
              description: `An account review for ${selectedOrg?.name || "an organization"} has been submitted and needs your review. Review date: ${reviewDate}`,
              assigned_to: reviewerId,
              created_by: user?.id ?? null,
              due_date: null,
              contact_id: null,
              event_id: null,
              // tasks.status is the task_status enum and tasks.priority an INTEGER 1-5. This
              // used to send priority "medium", which Postgres refused (22P02), and the catch
              // below swallowed it, so no reviewer ever received this task.
              status: TaskStatus.TODO,
              priority: TaskPriority.MEDIUM,
              organization_id: organizationId || null,
            });
          }
        } catch (assignErr) {
          // Don't block the save if reviewer assignment fails, but leave a trace.
          console.error(
            "[AccountReview] reviewer assignment / review task failed:",
            assignErr,
          );
        }
      }

      if (isNew) {
        navigate(`/account-reviews/${data.id}`, { replace: true });
      } else {
        setIsEditing(false);
      }
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: () => {
      if (!user?.id) {
        throw new Error("Not authenticated");
      }
      return createReviewComment(id!, newComment, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review-comments", id] });
      setNewComment("");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteAccountReview(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-reviews"] });
      navigate("/account-reviews", { replace: true });
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const handleSave = () => {
    if (!organizationId) {
      setError("Please select an organization");
      return;
    }
    if (!reviewDate) {
      setError("Review date is required");
      return;
    }
    setError(null);
    saveMutation.mutate();
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isNew && !review) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Review not found.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/account-reviews")}
            className="text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {isNew
                ? "New Account Review"
                : `Review: ${review?.organization?.name || "—"}`}
            </h1>
            {!isNew && review && (
              <p className="text-sm text-gray-500">
                {new Date(review.review_date).toLocaleDateString()}
                {review.submitted_by_profile &&
                  ` · Submitted by ${review.submitted_by_profile.full_name}`}
              </p>
            )}
          </div>
        </div>

        {!isNew && !isEditing && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setIsEditing(true)}>
              <PencilIcon className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
              <TrashIcon className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Audit Info Bar */}
      {!isNew && review && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
          <span>
            Created {new Date(review.created_at).toLocaleDateString()}{" "}
            {review.submitted_by_profile &&
              `by ${review.submitted_by_profile.full_name}`}
          </span>
          {review.last_edited_by_profile && (
            <span>
              Last edited{" "}
              {review.last_edited_at
                ? new Date(review.last_edited_at).toLocaleDateString()
                : ""}{" "}
              by {review.last_edited_by_profile.full_name}
            </span>
          )}
          {review.reviewer_profile && (
            <span>Reviewer: {review.reviewer_profile.full_name}</span>
          )}
          {review.reviewed_at && (
            <span>
              Reviewed {new Date(review.reviewed_at).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {/* ============================================= */}
      {/* SECTION 1: Information */}
      {/* ============================================= */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Information</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Organization */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization *
            </label>
            {isEditing && !selectedOrg ? (
              <div>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={orgSearchQuery}
                    onChange={(e) => setOrgSearchQuery(e.target.value)}
                    placeholder="Search organizations..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                </div>
                {orgSearchLoading && (
                  <div className="mt-2">
                    <LoadingSpinner />
                  </div>
                )}
                {orgSearchResults.length > 0 && (
                  <div className="mt-1 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {orgSearchResults.map((org) => (
                      <div
                        key={org.id}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedOrg({ id: org.id, name: org.name });
                          setOrganizationId(org.id);
                          setOrgSearchQuery("");
                          setOrgSearchResults([]);
                        }}
                      >
                        <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium">{org.name}</div>
                          <div className="text-xs text-gray-500">
                            {[org.address_city, org.address_state]
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-900">
                  {selectedOrg?.name || review?.organization?.name || "—"}
                </span>
                {isEditing && selectedOrg && (
                  <button
                    onClick={() => {
                      setSelectedOrg(null);
                      setOrganizationId("");
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 ml-2"
                  >
                    Change
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Review Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review Date *
            </label>
            {isEditing ? (
              <input
                type="date"
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900">
                {review?.review_date
                  ? new Date(review.review_date).toLocaleDateString()
                  : "—"}
              </p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            {isEditing ? (
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ReviewStatus)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {REVIEW_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <Badge
                color={
                  REVIEW_STATUS_OPTIONS.find((o) => o.value === review?.status)
                    ?.color || "gray"
                }
              >
                {REVIEW_STATUS_OPTIONS.find((o) => o.value === review?.status)
                  ?.label || "—"}
              </Badge>
            )}
          </div>

          {/* FreshService Ticket */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              FreshService Ticket Created
            </label>
            {isEditing ? (
              <BooleanToggle
                value={freshserviceTicketCreated}
                onChange={setFreshserviceTicketCreated}
              />
            ) : (
              <StatusIcon
                value={review?.freshservice_ticket_created ?? false}
              />
            )}
          </div>
        </div>
      </div>

      {/* ============================================= */}
      {/* SECTION 2: Other Items */}
      {/* ============================================= */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Other Items</h2>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">
            Evidence of Competitor Materials
          </span>
          {isEditing ? (
            <BooleanToggle
              value={evidenceOfCompetitorMaterials}
              onChange={setEvidenceOfCompetitorMaterials}
            />
          ) : (
            <StatusIcon
              value={review?.evidence_of_competitor_materials ?? false}
            />
          )}
        </div>
      </div>

      {/* ============================================= */}
      {/* SECTION 3: Retailer Materials */}
      {/* ============================================= */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Retailer Materials
        </h2>

        {[
          {
            label: "Counter Cards on Display",
            value: isEditing
              ? counterCardsOnDisplay
              : review?.counter_cards_on_display,
            onChange: setCounterCardsOnDisplay,
          },
          {
            label: "Acrylic Stand in Good Shape",
            value: isEditing
              ? acrylicStandInGoodShape
              : review?.acrylic_stand_in_good_shape,
            onChange: setAcrylicStandInGoodShape,
          },
          {
            label: "Posters",
            value: isEditing ? posters : review?.posters,
            onChange: setPosters,
          },
          {
            label: "Banners",
            value: isEditing ? banners : review?.banners,
            onChange: setBanners,
          },
          {
            label: "DealerShield Window Cling",
            value: isEditing
              ? dealershieldWindowCling
              : review?.dealershield_window_cling,
            onChange: setDealershieldWindowCling,
          },
        ].map((item) => (
          <div key={item.label} className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{item.label}</span>
            {isEditing ? (
              <MaterialStatusGroup
                value={item.value as MaterialStatus | null}
                onChange={item.onChange}
              />
            ) : (
              <StatusIcon value={item.value as MaterialStatus | null} />
            )}
          </div>
        ))}
      </div>

      {/* ============================================= */}
      {/* SECTION 4: Technology and Software */}
      {/* ============================================= */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Technology and Software
        </h2>

        {[
          {
            label: "Counter Card QR Verification",
            value: isEditing
              ? counterCardQrVerification
              : review?.counter_card_qr_verification,
            onChange: setCounterCardQrVerification,
          },
          {
            label: "Classroom Posters QR Verification",
            value: isEditing
              ? classroomPostersQrVerification
              : review?.classroom_posters_qr_verification,
            onChange: setClassroomPostersQrVerification,
          },
          {
            label: "Promo Code QR Verification",
            value: isEditing
              ? promoCodeQrVerification
              : review?.promo_code_qr_verification,
            onChange: setPromoCodeQrVerification,
          },
          {
            label: "Affiliate Link on Website",
            value: isEditing
              ? affiliateLinkOnWebsite
              : review?.affiliate_link_on_website,
            onChange: setAffiliateLinkOnWebsite,
          },
          {
            label: "Marketing Videos Playing",
            value: isEditing
              ? marketingVideosPlaying
              : review?.marketing_videos_playing,
            onChange: setMarketingVideosPlaying,
          },
          {
            label: "Uses GearFire/Axis POS",
            value: isEditing
              ? usesGearfireAxisPos
              : review?.uses_gearfire_axis_pos,
            onChange: setUsesGearfireAxisPos,
          },
        ].map((item) => (
          <div key={item.label} className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{item.label}</span>
            {isEditing ? (
              <BooleanToggle
                value={item.value as boolean}
                onChange={item.onChange}
              />
            ) : (
              <StatusIcon value={item.value as boolean} />
            )}
          </div>
        ))}
      </div>

      {/* ============================================= */}
      {/* SECTION 5: Events and Opportunities */}
      {/* ============================================= */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Events and Opportunities
        </h2>

        {[
          {
            label: "Attending Classes",
            value: isEditing ? attendingClasses : review?.attending_classes,
            onChange: setAttendingClasses,
          },
          {
            label: "USLS Certified Instructors",
            value: isEditing
              ? uslsCertifiedInstructors
              : review?.usls_certified_instructors,
            onChange: setUslsCertifiedInstructors,
          },
        ].map((item) => (
          <div key={item.label} className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{item.label}</span>
            {isEditing ? (
              <BooleanToggle
                value={item.value as boolean}
                onChange={item.onChange}
              />
            ) : (
              <StatusIcon value={item.value as boolean} />
            )}
          </div>
        ))}
      </div>

      {/* ============================================= */}
      {/* SECTION 6: Review Notes */}
      {/* ============================================= */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Review Notes</h2>

        {isEditing ? (
          <textarea
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            rows={4}
            placeholder="Notes from the visit..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        ) : (
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {review?.review_comment || "No notes"}
          </p>
        )}
      </div>

      {/* Save / Cancel */}
      {isEditing && (
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending
              ? "Saving..."
              : status === "submitted"
                ? "Submit for Review"
                : isNew
                  ? "Save Draft"
                  : "Save Changes"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              if (isNew) {
                navigate("/account-reviews");
              } else {
                setIsEditing(false);
              }
            }}
          >
            Cancel
          </Button>
        </div>
      )}

      {/* ============================================= */}
      {/* SECTION 7: Comments / Activity */}
      {/* ============================================= */}
      {!isNew && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ChatBubbleLeftIcon className="h-5 w-5" />
            Comments ({comments?.length || 0})
          </h2>

          {/* Comment list */}
          {(comments || []).length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No comments yet
            </p>
          ) : (
            <div className="space-y-3">
              {(comments || []).map((comment) => (
                <div key={comment.id} className="flex gap-3 py-2">
                  <UserCircleIcon className="h-8 w-8 text-gray-300 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {comment.user_profile?.full_name || "Unknown"}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                      {comment.comment_text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add comment */}
          <div className="border-t pt-4">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={2}
              placeholder="Add a comment..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <div className="flex justify-end mt-2">
              <Button
                size="sm"
                onClick={() => addCommentMutation.mutate()}
                disabled={!newComment.trim() || addCommentMutation.isPending}
              >
                {addCommentMutation.isPending ? "Posting..." : "Post Comment"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-2">Delete Review?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will permanently delete this account review and all its
              comments.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountReviewDetail;
